/**
 * ResumeSetupPrompt — "you were halfway through building a Genie, want to
 * finish it?", brought to the customer instead of parked on a page they have
 * to go and find.
 *
 * Deliberately self-contained: it fetches its own data, resumes on its own,
 * and renders nothing at all when there is nothing to resume. Drop it into any
 * surface with `<ResumeSetupPrompt />` and no props. It is built this way so
 * the dashboard's next-action band can adopt it without inheriting any
 * plumbing (see the cross-branch note in the branch report).
 *
 * Every abandoned setup is a customer who wanted the product and did not get
 * it, and they are the cheapest customers in the world to win back, because
 * they already said yes once.
 */
import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, Clock, Loader2, X } from "lucide-react";
import { toast } from "sonner";
import { useUnfinishedSetups } from "@/hooks/agent/useUnfinishedSetups";
import { SetupRecoveryService } from "@/services/agent/recovery";
import { cn } from "@/lib/utils";
import { GenieIcon } from "@/components/ui/genie-icons/GenieIcon";

interface ResumeSetupPromptProps {
  /**
   * Only prompt about setups abandoned within this window. Older ones stay on
   * /genie-hub/unfinished-setups rather than nagging from the dashboard.
   * Default 7 days.
   */
  withinDays?: number;
  className?: string;
}

const HOUR_MS = 3_600_000;

/** "20 minutes ago" / "3 hours ago" / "2 days ago". */
const sinceLabel = (iso: string): string => {
  // Setup timestamps come back without a zone marker; treat them as UTC, the
  // same way the unfinished-setups list does.
  const then = new Date(`${iso}Z`).getTime();
  const elapsed = Date.now() - then;
  if (Number.isNaN(elapsed)) return "recently";

  const minutes = Math.floor(elapsed / 60_000);
  if (minutes < 60) return `${Math.max(minutes, 1)} minute${minutes === 1 ? "" : "s"} ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hour${hours === 1 ? "" : "s"} ago`;

  const days = Math.floor(hours / 24);
  return `${days} day${days === 1 ? "" : "s"} ago`;
};

export const ResumeSetupPrompt = ({ withinDays = 7, className }: ResumeSetupPromptProps) => {
  const navigate = useNavigate();
  const { unfinishedSetups, isLoading, markAsAbandoned } = useUnfinishedSetups();
  const [resuming, setResuming] = useState(false);
  const [dismissed, setDismissed] = useState(false);

  if (isLoading || dismissed) return null;

  const cutoff = Date.now() - withinDays * 24 * HOUR_MS;
  const recent = unfinishedSetups.filter((setup) => {
    const started = new Date(`${setup.created_at}Z`).getTime();
    return !Number.isNaN(started) && started >= cutoff;
  });

  if (recent.length === 0) return null;

  const setup = recent[0];
  const conversationId = setup.elevenlabs_id;
  if (!conversationId) return null;

  const handleResume = async () => {
    setResuming(true);
    try {
      const { transcript, analysis } = await SetupRecoveryService.resumeSetupFromConversation(conversationId);

      sessionStorage.setItem(
        "resumedSetupData",
        JSON.stringify({ conversationId, transcript, analysis, isResumed: true }),
      );
      navigate("/user-hub/genie-setup");
    } catch {
      toast.error("We could not bring that setup back", {
        description: "You can start a new one and it will be saved as you go.",
        action: { label: "Start fresh", onClick: () => navigate("/welcome") },
      });
      setResuming(false);
    }
  };

  const handleDismiss = async () => {
    setDismissed(true);
    try {
      await markAsAbandoned(conversationId);
    } catch {
      // The prompt is already gone from view; nothing useful to say here.
    }
  };

  return (
    <div className={cn("p-[2px] rounded-2xl bg-gradient-to-r from-[#4E9CFF] to-[#FF4DB8]", className)}>
      <div className="rounded-[14px] bg-white p-5 md:p-6">
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3 min-w-0">
            <GenieIcon name="genie-lamp" size={32} className="mt-0.5 h-8 w-8 flex-shrink-0" />
            <div className="min-w-0">
              <h3 className="text-base font-semibold text-gray-900">
                You were partway through building a Genie
              </h3>
              <p className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-1 text-sm text-gray-600">
                <span className="inline-flex items-center gap-1.5">
                  <Clock className="h-3.5 w-3.5 text-gray-400" />
                  Started {sinceLabel(setup.created_at)}
                </span>
                <span className="text-gray-300">·</span>
                <span>
                  {setup.hasPersistedState
                    ? "Everything you set up is saved."
                    : "Pick up where you left off."}
                </span>
              </p>
              {recent.length > 1 && (
                <button
                  type="button"
                  onClick={() => navigate("/genie-hub/unfinished-setups")}
                  className="mt-1.5 text-xs font-semibold text-genie-primary hover:underline"
                >
                  {recent.length - 1} more unfinished
                </button>
              )}
            </div>
          </div>

          <div className="flex items-center gap-2 flex-shrink-0">
            <button
              type="button"
              onClick={() => void handleResume()}
              disabled={resuming}
              className="inline-flex items-center gap-2 rounded-xl px-5 py-3 text-sm font-semibold text-white shadow-lg shadow-genie-primary/30 transition-all duration-200 hover:opacity-95 disabled:opacity-60"
              style={{ background: "linear-gradient(90deg, #4E9CFF 0%, #FF4DB8 100%)" }}
            >
              {resuming ? <Loader2 className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
              {resuming ? "Bringing it back" : "Finish it"}
            </button>
            <button
              type="button"
              onClick={() => void handleDismiss()}
              aria-label="Dismiss this unfinished setup"
              className="flex h-9 w-9 items-center justify-center rounded-xl text-gray-400 transition-colors hover:bg-genie-background hover:text-gray-600"
            >
              <X className="h-4 w-4" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ResumeSetupPrompt;
